/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   CORE / DEALS — Pure-Engine der Deals (S169, Teil A · P1)
   =============================================================================
   Reine, DOM-freie Logik der Deals-Engine (Vitest-getestet). Zwei Aufgaben:

   1) VERFÜGBARKEIT — isDealAvailable(deal, now, { isOperator }):
      Spiegelt die Server-Filterung (active_deals_for_guest) fürs Front-End.
      Gast: nur im Fenster (available_days ISO-DOW + from/to). Operator/Cockpit:
      bei guest_only_window=true wird das Fenster ignoriert (Spät-Gast/Kulanz).

   2) DECOMPOSITION — resolveDealToOrderItems(selection, dealDetail, menuIndex):
      🔴 KORREKTUR (Jonas 2026-06-29, KDS-Befund): Der Deal nutzt die EXISTIERENDE
      Bestell-Struktur 1:1 — KEINE eigene Decomposition, keine neuen Keys/Labels,
      keine Deal-Gruppierung. Eine Normalbestellung ist EIN Food-order_item mit den
      bestehenden Customization-Keys (Beilage→`side`, Dips→`sideSauces`, Soßen→
      `sauces`, dazu `extras`/`bun`/`removedIngredients`; Beilagen-Preis im
      unit_price eingerechnet). Getränke sind separate Zeilen (19 %). Der Deal
      erzeugt EXAKT diese Struktur — nur mit Deal-Preis. Deal-Bezug nur als
      unsichtbare Meta (`customizations._dealMeta`) fürs Reporting, NICHT gerendert.

   PURITY: keine Date.now()/Math.random(). `now` + `line_uid` kommen vom Aufrufer.
   Snake_case (RPC) UND camelCase (gemappte Form) werden defensiv gelesen.
============================================================================ */

/* -------------------------------------------------------------------------- */
/* Zeit-/Fenster-Helfer                                                        */
/* -------------------------------------------------------------------------- */

/** Minuten seit Mitternacht aus einem Date (tenant-lokal interpretiert). */
export function dealMinutesOfDay(now) {
  const d = (now instanceof Date) ? now : new Date(now);
  return d.getHours() * 60 + d.getMinutes();
}

/** ISO-Wochentag 1=Mo … 7=So aus einem Date (JS getDay(): 0=So). */
export function dealIsoDow(now) {
  const d = (now instanceof Date) ? now : new Date(now);
  const js = d.getDay();
  return js === 0 ? 7 : js;
}

/** "HH:MM" / "HH:MM:SS" → Minuten seit Mitternacht; null bei leer/ungültig. */
export function parseTimeToMinutes(t) {
  if (t === null || t === undefined || t === '') return null;
  const m = String(t).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return (Number(m[1]) * 60) + Number(m[2]);
}

function _days(deal)      { return deal.available_days   ?? deal.availableDays   ?? null; }
function _from(deal)      { return deal.available_from   ?? deal.availableFrom   ?? null; }
function _to(deal)        { return deal.available_to     ?? deal.availableTo     ?? null; }
function _guestOnly(deal) { const v = deal.guest_only_window ?? deal.guestOnlyWindow; return v !== false; }
function _deleted(deal)   { return deal.deleted_at ?? deal.deletedAt ?? null; }

/** Liegt `now` im Verfügbarkeitsfenster (Tag + Uhrzeit)? Leere Tage/Zeiten = offen. */
export function isWithinDealWindow(deal, now) {
  if (!deal) return false;
  const days = _days(deal);
  if (Array.isArray(days) && days.length && !days.includes(dealIsoDow(now))) return false;
  const fromMin = parseTimeToMinutes(_from(deal));
  const toMin   = parseTimeToMinutes(_to(deal));
  const nowMin  = dealMinutesOfDay(now);
  if (fromMin !== null && nowMin < fromMin) return false;
  if (toMin   !== null && nowMin > toMin)   return false;
  return true;
}

/**
 * Ist der Deal zu `now` verfügbar? `opts.isOperator=true` → Cockpit/Operator-Sicht.
 * - inaktiv / soft-deleted → nie.
 * - Operator + guest_only_window=true → Fenster ignoriert (immer verfügbar).
 * - sonst (Gast, oder Operator bei guest_only_window=false) → Fenster muss passen.
 */
export function isDealAvailable(deal, now, opts = {}) {
  if (!deal) return false;
  if (deal.active === false) return false;
  if (_deleted(deal)) return false;
  if (opts.isOperator && _guestOnly(deal)) return true;
  return isWithinDealWindow(deal, now);
}

/** Filtert eine Deal-Liste auf die zu `now` verfügbaren (Single-Source fürs UI). */
export function filterAvailableDeals(deals, now, opts = {}) {
  return (Array.isArray(deals) ? deals : []).filter(d => isDealAvailable(d, now, opts));
}

/* -------------------------------------------------------------------------- */
/* Decomposition-Resolver                                                      */
/* -------------------------------------------------------------------------- */

function _grpId(g)   { return g.id ?? g.group_id; }
function _grpPrice(g){ return Number(g.deal_price_cents ?? g.dealPriceCents ?? 0); }
function _grpTax(g)  { return Number(g.tax_rate ?? g.taxRate ?? 7); }
function _addId(a)   { return a.id ?? a.addon_id; }
function _addPrice(a){ return Number(a.price_cents ?? a.priceCents ?? 0); }
function _addTax(a)  { return Number(a.tax_rate ?? a.taxRate ?? 7); }

/** Summe bezahlter Extras in Cent (P4: Extras kommen ON TOP der Hauptgericht-Zeile,
 *  Essen-Satz). extras = [{ id, name, price(€) }] aus der Picker-Anpassung. */
export function dealExtrasCents(extras) {
  return (Array.isArray(extras) ? extras : [])
    .reduce((s, e) => s + Math.round((Number(e.price) || 0) * 100), 0);
}

function _menuName(menuIndex, id, fallback) {
  const m = menuIndex && menuIndex[id];
  return (m && (m.name ?? m.name_snapshot)) || fallback || '(unbenannt)';
}
function _menuCat(menuIndex, id, fallback) {
  const m = menuIndex && menuIndex[id];
  return (m && (m.category ?? m.category_snapshot)) || fallback || null;
}

/**
 * Löst eine Deal-Auswahl in order_items-Drafts (camelCase) auf.
 *
 * selection = {
 *   groupId, mainItemId,
 *   customizations: { bun, patty, dressing, milk, removedIngredients, extras,
 *                     sauces, side, sideSauces },   // NORMALE Keys (wie lineToRpcItem)
 *   drink: { addonId, itemId } | null,              // separate Theken-Zeile (19 %)
 *   qty,            // Anzahl Deals (Default 1)
 *   lineUid         // unsichtbare Reporting-Klammer (vom Aufrufer)
 * }
 * dealDetail = verschachteltes deal_detail-jsonb (id, groups[…], addons[…]).
 * menuIndex  = { [menu_item_id]: { name, category, itemType } } (für Snapshots).
 *
 * Rückgabe: Array von Drafts { menuItemId, name, category, qty, unitPriceCents,
 * taxRate, customizations, notes } — EXAKT die Normal-order_item-Struktur:
 *   [0] Hauptgericht (Food, 7 %): normale Keys + Beilage in `side`/`sideSauces`,
 *       unit_price_cents = Gruppenpreis + Extras + Beilagen-Preis + Brötchen-Aufpreis
 *       (S198-HF2). `_dealMeta` (unsichtbar) verklammert fürs Reporting; optionaler
 *       Positions-Kommentar (`selection.notes`) landet als `notes` auf dieser Zeile.
 *   [1] Drink (optional, 19 %, Theke): eigene Zeile, trägt aber dasselbe `_dealMeta`
 *       (gleicher line_uid wie [0]) → Teil des Deals fürs Reporting + Rabatt-Ausschluss.
 * Wirft bei fehlender Gruppe/Add-on (Inkonsistenz sichtbar machen).
 */
export function resolveDealToOrderItems(selection, dealDetail, menuIndex) {
  if (!selection || !dealDetail) throw new Error('resolveDealToOrderItems: selection und dealDetail erforderlich');
  const dealId = dealDetail.id;
  const dealName = dealDetail.name || 'Aktion';
  const qty = Math.max(1, Number(selection.qty) || 1);
  const lineUid = selection.lineUid || `deal:${dealId}:${selection.groupId}`;

  const groups = Array.isArray(dealDetail.groups) ? dealDetail.groups : [];
  const group = groups.find(g => _grpId(g) === selection.groupId);
  if (!group) throw new Error(`resolveDealToOrderItems: Gruppe ${selection.groupId} nicht im Deal`);

  const c = selection.customizations || {};
  // Beilage steckt im normalen `side`-Key {id,name,price}; ihr Preis ist im
  // Deal-Preis EINGERECHNET (z. B. 10 € Gruppe + 3 € Beilage = 13 €), genau wie
  // bei der Normalbestellung (Beilagen-Preis Teil von unit_price_cents).
  const sidePriceCents = Math.round((Number(c.side && c.side.price) || 0) * 100);
  // S198-HF2 (Jonas, DB-Befund): Brötchen-Aufpreis (Whitelist, z. B. glutenfrei
  // +1,20 €) MUSS eingerechnet werden — bun.extra steht in den customizations, fehlte
  // aber in der Preis-Summe (unit 1300 statt 1420). Wie bun-Fee der Normalbestellung.
  const bunExtraCents = Math.round((Number(c.bun && c.bun.extra) || 0) * 100);
  // S198-HF1: optionaler Positions-Kommentar (Cockpit) landet auf der FOOD-Zeile.
  const foodNotes = (selection.notes && String(selection.notes).trim()) || null;
  const drafts = [];

  // 1) Hauptgericht = EIN Food-order_item wie die Normalbestellung. Alle bestehenden
  //    Customization-Keys, Deal-Preis = Gruppenpreis + Extras (on top) + Beilage,
  //    tax_rate der Gruppe (Essen). `_dealMeta` ist unsichtbar (nicht gerendert).
  drafts.push({
    menuItemId: selection.mainItemId,
    name:       _menuName(menuIndex, selection.mainItemId, selection.mainName),
    category:   _menuCat(menuIndex, selection.mainItemId, selection.mainCategory),
    qty,
    unitPriceCents: _grpPrice(group) + dealExtrasCents(c.extras) + sidePriceCents + bunExtraCents,
    taxRate:        _grpTax(group),
    customizations: {
      bun:                c.bun || null,
      patty:              c.patty || null,
      dressing:           c.dressing || null,
      milk:               c.milk || null,
      removedIngredients: c.removedIngredients || [],
      extras:             c.extras || [],
      sauces:             c.sauces || [],
      side:               c.side || null,
      sideSauces:         c.sideSauces || [],
      _dealMeta:          { id: dealId, name: dealName, line_uid: lineUid }
    },
    notes: foodNotes
  });

  // 2) Drink (optional) = separate 19-%-Theke-Zeile, trägt aber DASSELBE `_dealMeta`
  //    (gleicher line_uid wie das Hauptgericht). Damit ist der Drink Teil des Deals:
  //    er rollt im Reporting (stats_deal_sales, count via distinct line_uid) in den
  //    Deal UND ist — wie alle Deal-Positionen — von Prozent-Rabatten ausgeschlossen
  //    (DB-RPCs phase_132/133/134 filtern Deals über customizations->'_dealMeta').
  if (selection.drink && selection.drink.itemId) {
    const addons = Array.isArray(dealDetail.addons) ? dealDetail.addons : [];
    const addon = addons.find(a => _addId(a) === selection.drink.addonId);
    if (!addon) throw new Error(`resolveDealToOrderItems: Add-on (drink) ${selection.drink.addonId} nicht im Deal`);
    drafts.push({
      menuItemId: selection.drink.itemId,
      name:       _menuName(menuIndex, selection.drink.itemId, selection.drink.itemName),
      category:   _menuCat(menuIndex, selection.drink.itemId, selection.drink.itemCategory),
      qty,
      unitPriceCents: _addPrice(addon),
      taxRate:        _addTax(addon),
      customizations: {
        bun: null, patty: null, dressing: null, milk: null,
        removedIngredients: [], extras: [], sauces: [], side: null, sideSauces: [],
        _dealMeta:          { id: dealId, name: dealName, line_uid: lineUid }
      },
      notes: null
    });
  }

  return drafts;
}

/** Gesamt-Brutto (Cents) einer Deal-Position = Σ Komponenten-Drafts (unit × qty). */
export function dealDraftsTotalCents(drafts) {
  return (Array.isArray(drafts) ? drafts : [])
    .reduce((s, d) => s + (Number(d.unitPriceCents) || 0) * (Number(d.qty) || 1), 0);
}

/** Unsichtbare Deal-Reporting-Meta eines order_items ({id,name,line_uid}) oder null. */
export function dealMetaOf(orderItem) {
  const c = orderItem && orderItem.customizations;
  return (c && c._dealMeta) || null;
}

/* -------------------------------------------------------------------------- */
/* S200 — Menü-Gruppierung fürs gruppierte Kassieren/Stornieren                */
/* -------------------------------------------------------------------------- */

/**
 * Gruppiert order_items nach `customizations._dealMeta.line_uid` — Menü-Komponenten
 * mit gleichem line_uid landen in EINER Gruppe (Optik/Logik-Klammer für Split +
 * Storno). Reihenfolge = Erst-Auftreten (Gruppe erscheint an der Position ihrer
 * ersten Komponente; weitere Komponenten reihen sich dort ein). `_dealMeta` bleibt
 * unangetastet (Rabatt-Ausschluss + Reporting intakt). Pure.
 *
 * @returns {Array<{type:'group', lineUid, dealName, items:Array}|{type:'single', item}>}
 */
export function groupByLineUid(items) {
  const map = new Map();   // line_uid → Gruppen-Eintrag
  const out = [];
  (Array.isArray(items) ? items : []).forEach(it => {
    // line_uid entweder aus dem order_item (customizations._dealMeta) ODER aus einem
    // flachen Feld (`lineUid`) — Letzteres nutzt der Storno-Modal-State (keine customizations).
    const meta = it && it.customizations && it.customizations._dealMeta;
    const uid = (meta && meta.line_uid) || (it && it.lineUid) || null;
    if (uid) {
      let g = map.get(uid);
      if (!g) { g = { type: 'group', lineUid: uid, dealName: (meta && meta.name) || (it && it.dealName) || 'Menü', items: [] }; map.set(uid, g); out.push(g); }
      g.items.push(it);
    } else {
      out.push({ type: 'single', item: it });
    }
  });
  return out;
}

/** Pure: gibt es rabattierbare (Nicht-Deal-)Positionen? Deals (mit `_dealMeta`) sind
 *  von Prozent-Rabatten ausgeschlossen (S179 R3). Single-Source für den Rabatt-Gate. */
export function hasDiscountableItems(items) {
  return (Array.isArray(items) ? items : []).some(it => !(it && it.customizations && it.customizations._dealMeta));
}

/* -------------------------------------------------------------------------- */
/* S198 Cut b3 — RÜCKWÄRTS-Mapper (Pure Inverse zu resolveDealToOrderItems)     */
/* -------------------------------------------------------------------------- */

function _rowMid(r)  { return r && (r.menuItemId ?? r.menu_item_id ?? null); }
function _rowCust(r) { const c = r && r.customizations; return (c && typeof c === 'object') ? c : {}; }
function _rowMeta(r) { const c = _rowCust(r); return c._dealMeta || null; }
function _itemMid(it){ return it && (it.menu_item_id ?? it.menuItemId ?? null); }

/**
 * Löst das persistierte order_items-PAAR (Food + optional Drink, verklammert über
 * `customizations._dealMeta.line_uid`) zurück in eine `selection` — INVERS zu
 * resolveDealToOrderItems. Für den „Deal-Position bearbeiten"-Re-Edit (Warenkorb).
 *
 * Roundtrip-Garantie (Vitest): bei unverändertem `dealDetail` gilt
 *   resolveDealToOrderItems(orderItemsToDealSelection(rows, dealDetail), dealDetail, menuIndex) ≙ rows.
 *
 * Auflösungs-Regeln (Handoff §Rückwärts-Mapping):
 * - `groupId` steht NICHT in der Zeile → aus `mainItemId` gegen `dealDetail.groups[].items`
 *   auflösen (NIE aus line_uid parsen — Format ist nicht garantiert).
 * - `drink.addonId` aus der Drink-Zeile (`menu_item_id`) gegen die Drink-`addons` auflösen.
 * - Feld-tolerant: liest DB-Shape (menu_item_id/name_snapshot) UND camelCase-RPC-Shape.
 *
 * @returns {object|null} selection oder null (kein Deal / Gruppe nicht mehr auflösbar
 *          / _dealMeta inkonsistent → nicht editierbar, Aufrufer zeigt Hinweis).
 */
export function orderItemsToDealSelection(rows, dealDetail) {
  if (!dealDetail || !Array.isArray(rows) || !rows.length) return null;

  // INVERSER Deal-Guard: alle Zeilen MÜSSEN _dealMeta tragen und denselben line_uid.
  const metas = rows.map(_rowMeta);
  if (metas.some(m => !m)) return null;
  const lineUid = metas[0].line_uid;
  if (!lineUid || metas.some(m => m.line_uid !== lineUid)) return null;

  const groups = Array.isArray(dealDetail.groups) ? dealDetail.groups : [];
  const addons = Array.isArray(dealDetail.addons) ? dealDetail.addons : [];
  const drinkAddons = addons.filter(a => (a.kind ?? a.addon_kind) === 'drink');

  // Food-Zeile = die Zeile, deren menu_item_id in einer Deal-Gruppe liegt → groupId.
  let foodRow = null, groupId = null;
  for (const r of rows) {
    const id = _rowMid(r);
    const g = groups.find(gr => (Array.isArray(gr.items) ? gr.items : []).some(it => _itemMid(it) === id));
    if (g) { foodRow = r; groupId = _grpId(g); break; }
  }
  if (!foodRow || groupId == null) return null; // Deal/Gruppe nicht mehr auflösbar

  // Drink-Zeile (optional) = andere Zeile, deren menu_item_id in einem Drink-Add-on liegt.
  let drink = null;
  for (const r of rows) {
    if (r === foodRow) continue;
    const id = _rowMid(r);
    const a = drinkAddons.find(ad => (Array.isArray(ad.items) ? ad.items : []).some(it => _itemMid(it) === id));
    if (a) { drink = { addonId: _addId(a), itemId: id }; break; }
  }

  const fc = _rowCust(foodRow);
  const arr = v => Array.isArray(v) ? v.slice() : [];
  return {
    groupId,
    mainItemId:   _rowMid(foodRow),
    mainName:     foodRow.nameSnapshot ?? foodRow.name_snapshot ?? foodRow.name ?? null,
    mainCategory: foodRow.categorySnapshot ?? foodRow.category_snapshot ?? foodRow.category ?? null,
    qty:          Math.max(1, Number(foodRow.qty) || 1),
    lineUid,
    notes:        (foodRow.notes ?? foodRow.note ?? null),   // S198-HF1 — Food-Zeilen-Kommentar
    customizations: {
      bun:      fc.bun || null,
      patty:    fc.patty || null,
      dressing: fc.dressing || null,
      milk:     fc.milk || null,
      removedIngredients: arr(fc.removedIngredients),
      extras:             arr(fc.extras),
      sauces:             arr(fc.sauces),
      side:     fc.side || null,
      sideSauces:         arr(fc.sideSauces)
    },
    drink
  };
}
